import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Chip, IconButton, Tooltip, TextField,
    InputAdornment, Button, CircularProgress, Stack, Menu, MenuItem,
    ThemeProvider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import { useAuth } from '../../components/AuthProvider';
import Swal from 'sweetalert2';
import lightTheme from '../../themes/lightTheme';
import CreateTokenDialog from './CreateTokenDialog';

function fmt(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR');
}

const VENDOR_COLORS = {
    'dns_blacklist': 'error',
    'dns_acl':       'info',
    'dns_localzone': 'warning',
    'dns_node':      'default',
    '*':             'secondary',
};

function scopeColor(scope) {
    const prefix = scope.split(':')[0];
    return VENDOR_COLORS[prefix] || 'default';
}

export default function ApiTokensPage() {
    const notify = useNotify();
    const auth = useAuth();
    const navigate = useNavigate();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuTarget, setMenuTarget] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await api.post('/api-tokens/', {
                limit: 100, offset: 0,
                name_contains: query || null,
            });
            setTokens(res.data?.data || []);
        } catch (e) {
            notify.setNotify({
                open: true,
                message: e.response?.data?.detail || 'Erro ao listar tokens',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'IPV7 Tools - Tokens API';
        reload();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const t = setTimeout(reload, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [query]);

    const handleRevoke = async (tok) => {
        const r = await Swal.fire({
            title: `Revogar token "${tok.name}"?`,
            text: 'Requests subsequentes com esse token vão receber 401 imediatamente.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Revogar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#aa2525',
            didOpen: () => {
                const c = document.querySelector('.swal2-container');
                if (c) c.style.zIndex = '99999';
            },
        });
        if (!r.isConfirmed) return;
        try {
            await api.post(`/api-tokens/${tok.id}/revoke`);
            notify.setNotify({ open: true, message: 'Token revogado', severity: 'success' });
            reload();
        } catch (e) {
            notify.setNotify({
                open: true,
                message: e.response?.data?.detail || 'Erro ao revogar',
                severity: 'error',
            });
        }
    };

    const handleDelete = async (tok) => {
        const r = await Swal.fire({
            title: `Apagar token "${tok.name}"?`,
            text: 'Soft-delete — preserva histórico mas remove da listagem. Use Revogar pra suspender sem apagar.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Apagar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#aa2525',
            didOpen: () => {
                const c = document.querySelector('.swal2-container');
                if (c) c.style.zIndex = '99999';
            },
        });
        if (!r.isConfirmed) return;
        try {
            await api.delete(`/api-tokens/${tok.id}`);
            notify.setNotify({ open: true, message: 'Token apagado', severity: 'success' });
            reload();
        } catch (e) {
            notify.setNotify({
                open: true,
                message: e.response?.data?.detail || 'Erro ao apagar',
                severity: 'error',
            });
        }
    };

    const openMenu = (e, tok) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuTarget(tok);
    };
    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuTarget(null);
    };

    const canCreate = auth.hasPerm('api_token.create');
    const canRevoke = auth.hasPerm('api_token.update');
    const canDelete = auth.hasPerm('api_token.delete');

    const StatusChip = ({ tok }) => {
        if (tok.revoked_at) {
            return <Chip size="small" icon={<BlockIcon />} label="Revogado" color="error" variant="outlined" />;
        }
        if (tok.expires_at && new Date(tok.expires_at) <= new Date()) {
            return <Chip size="small" icon={<HourglassBottomIcon />} label="Expirado" color="warning" variant="outlined" />;
        }
        return <Chip size="small" icon={<CheckCircleIcon />} label="Ativo" color="success" variant="outlined" />;
    };

    return (
        <ThemeProvider theme={lightTheme}>
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: 'calc(100vh - 100px)', p: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                    Tokens API
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Buscar token…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        }}
                        sx={{ width: 240 }}
                    />
                    <Button
                        startIcon={<MenuBookIcon />}
                        variant="outlined"
                        onClick={() => navigate('/api_tokens/docs')}
                    >
                        Documentação API
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setCreateOpen(true)}
                        disabled={!canCreate}
                    >
                        Novo token
                    </Button>
                </Stack>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Tokens pra autenticação de scripts/integrações automatizadas
                (header <code>Authorization: Token &lt;raw&gt;</code>). O raw é
                mostrado apenas uma vez, na criação. Revogar = suspender imediato.
            </Typography>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : tokens.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {query ? 'Nenhum token encontrado.' : 'Nenhum token cadastrado.'}
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {tokens.map((tok) => (
                        <Grid item xs={12} sm={6} md={4} key={tok.id}>
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    minHeight: 160,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    borderLeft: '4px solid',
                                    borderColor: tok.is_active ? 'success.main' : 'grey.400',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <KeyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 16, wordBreak: 'break-all' }}>
                                            {tok.name}
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={(e) => openMenu(e, tok)} disabled={!canRevoke && !canDelete}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <StatusChip tok={tok} />

                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                        Scopes
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                        {(tok.scopes || []).map((s) => (
                                            <Chip
                                                key={s}
                                                size="small"
                                                label={s}
                                                color={scopeColor(s)}
                                                variant="outlined"
                                                sx={{ fontFamily: 'monospace', fontSize: 11 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>

                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                        Último uso: {fmt(tok.last_used_at)}
                                    </Typography>
                                    {tok.expires_at && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                            Expira: {fmt(tok.expires_at)}
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                <MenuItem
                    onClick={() => { handleRevoke(menuTarget); closeMenu(); }}
                    disabled={!canRevoke || (menuTarget?.revoked_at !== null && menuTarget?.revoked_at !== undefined)}
                >
                    <BlockIcon fontSize="small" sx={{ mr: 1 }} /> Revogar
                </MenuItem>
                <MenuItem
                    onClick={() => { handleDelete(menuTarget); closeMenu(); }}
                    disabled={!canDelete}
                    sx={{ color: 'error.main' }}
                >
                    Apagar
                </MenuItem>
            </Menu>

            <CreateTokenDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => { setCreateOpen(false); reload(); }}
            />
        </Box>
        </ThemeProvider>
    );
}
